import React from 'react';

import {
    Button,
    ButtonProps,
    Container,
    Text,
    TextProps,
    Title,
    TitleProps,
} from '@soluto-design/react';
import cls from '@soluto-design/styles/cls';

import Iframe from '../../atoms/Iframe';
import Media, { MediaProps } from '../../molecules/Media';

export interface BlockContentLargeSectionProps {
    preTitle?: string;
    preTitleTag?: TextProps['component'];
    title?: string;
    titleTag?: TitleProps['component'];
    description?: string;
    descriptionLarge?: boolean;
    image?: MediaProps['media'];
    imageRight?: boolean;
    imageWithFrame?: boolean;
    imageRounded?: boolean;
    cta?: ButtonProps;
    iframeUrl?: string;
}

export default function BlockContentLargeSection({
    preTitle,
    preTitleTag = 'p',
    title,
    titleTag = 'h2',
    description,
    descriptionLarge = false,
    image,
    imageRight = false,
    imageWithFrame = false,
    imageRounded = false,
    cta,
    iframeUrl,
}: BlockContentLargeSectionProps) {
    return (
        <section className="my-xxxl">
            <Container className="row">
                <div
                    className={cls(
                        'col-span-full hd:col-span-10 hd:col-start-2 hd:col-end-12 flex flex-col lg:flex-row gap-48 hd:grid hd:grid-cols-subgrid hd:gap-gutter',
                        {
                            'md:gap-64 lg:gap-auto': descriptionLarge,
                            'lg:gap-32 xl:gap-64 md:col-span-6 lg:col-span-full':
                                !descriptionLarge,
                        },
                    )}>
                    <div
                        className={cls(
                            'lg:basis-1/2 flex flex-col justify-center',
                            {
                                'lg:order-2 hd:col-start-6 hd:col-end-11 hd:order-2':
                                    imageRight,
                                'hd:col-start-1 hd:col-end-6 hd:order-1':
                                    !imageRight,
                            },
                        )}>
                        {imageWithFrame && (image || iframeUrl) ? (
                            <div
                                className={cls(
                                    'rounded-medium aspect-square p-[18%] bg-base-100 flex items-center justify-center relative',
                                )}>
                                <>
                                    {iframeUrl ? (
                                        <Iframe
                                            url={iframeUrl}
                                            title={title as string}
                                            className="h-full w-full"
                                        />
                                    ) : !!image ? (
                                        <Media
                                            media={image}
                                            {...(!image.isImage && {
                                                autoPlay: true,
                                            })}
                                            layout="responsive"
                                            className={cls(
                                                'w-full',
                                                imageRounded &&
                                                    'rounded-medium',
                                            )}
                                        />
                                    ) : null}
                                </>
                            </div>
                        ) : !imageWithFrame && (image || iframeUrl) ? (
                            <>
                                {iframeUrl ? (
                                    <Iframe
                                        url={iframeUrl}
                                        title={title as string}
                                        className="min-h-[375px] md:min-h-[500px] max-w-full"
                                    />
                                ) : !!image ? (
                                    <Media
                                        media={image}
                                        {...(!image.isImage && {
                                            autoPlay: true,
                                        })}
                                        layout="responsive"
                                        className={cls(
                                            'w-full',
                                            imageRounded && 'rounded-medium',
                                        )}
                                    />
                                ) : null}
                            </>
                        ) : null}
                    </div>
                    <div
                        className={cls(
                            'lg:basis-1/2 flex flex-col justify-center',
                            {
                                'lg:order-1 hd:col-start-1 hd:col-end-5 hd:order-1':
                                    imageRight,
                                'hd:col-start-7 hd:col-end-11 hd:order-2':
                                    !imageRight,
                                'xl:pl-64 hd:pl-0 ':
                                    !descriptionLarge && imageRight,
                                'xl:pr-64 hd:pr-0 ':
                                    !descriptionLarge && !imageRight,
                            },
                        )}>
                        {preTitle && (
                            <Text
                                component={preTitleTag}
                                className="text-c100 text-text-primary mb-16 uppercase">
                                {preTitle}
                            </Text>
                        )}
                        {!!title && (
                            <Title
                                component={titleTag}
                                className="text-h300 text-text-primary pb-lg">
                                {title}
                            </Title>
                        )}
                        {description && (
                            <Text
                                isHtml
                                component="div"
                                className={cls(
                                    'text-text-primary text-spacers',
                                    {
                                        'text-b200': descriptionLarge,
                                        'text-b300 [&_ul]:text-b400 unordered-list-items blockquote-list-icon':
                                            !descriptionLarge,
                                    },
                                )}>
                                {description}
                            </Text>
                        )}
                        {cta && (
                            <Button
                                {...cta}
                                variant="primary"
                                size="large"
                                className="mt-lg"
                            />
                        )}
                    </div>
                </div>
            </Container>
        </section>
    );
}
